import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import { Alert, AlertTitle, Box, Container, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useBulkUpdateGHADiscountRules } from '~/hooks/apis/connection-google/useGHADiscountRules';

import {
  BlacklistAction,
  BulkSwitchGHAPropertiesAction,
  BulkUpdateGHADiscountRulesAccountType,
  BulkUpdateGHADiscountRulesErrorResponse,
  CUGWhitelistAction,
  CugTier,
  GHAAccountType,
  GHAPropertyBrand,
  GHAPropertyType,
  bulkAppendPricingRegions,
  bulkBlacklistProperties,
  bulkCUGWhitelistProperties,
  bulkSetCugTier,
  bulkSwitchGHAProperties,
  bulkUpdateGHAPropertyType,
} from '~/services/ConnectionGoogleService';

import GHABulkOperationDialog from './GHABulkOperationDialog';
import GHABulkOperationsMenu from './GHABulkOperationsMenu';
import GHAPropertyListMetadata from './GHAPropertyListMetadata';
import GHAPropertySearch from './GHAPropertySearch';
import { CUG_TIER_LABELS, CUG_TIER_OPTIONS, CUG_TIER_VALUES } from './constants';
import { getAccountTypeOptionsForBrand } from './utils';

type DialogType =
  | 'switch-properties'
  | 'update-property-type'
  | 'blacklist-properties'
  | 'cug-whitelist-properties'
  | 'cug-tier-properties'
  | 'bulk-pricing-regions'
  | 'bulk-discounts'
  | null;
const supportedBrands: Array<GHAPropertyBrand> = ['luxuryescapes', 'scoopontravel'];

function getBulkDiscountDialogErrorMessage(error: unknown) {
  const apiError = error as {
    message?: string;
    status?: number;
    responseBody?: BulkUpdateGHADiscountRulesErrorResponse;
  };

  if (apiError.status === 400 && apiError.responseBody?.error) {
    return apiError.responseBody.error;
  }

  return apiError.message || 'An unexpected error occurred';
}

function GHAPropertyManagementContainer() {
  const [openDialog, setOpenDialog] = useState<DialogType>(null);
  const bulkUpdateDiscountRules = useBulkUpdateGHADiscountRules();

  const currentBrand = useSelector((state: App.State) => state.tenant?.brand);
  const isSupported = supportedBrands.includes(currentBrand as GHAPropertyBrand);

  const brandAccountTypeOptions = useMemo(
    () => getAccountTypeOptionsForBrand(currentBrand as GHAPropertyBrand),
    [currentBrand],
  );

  const handleSwitchProperties = async (file: File, action: string, accountType?: string) => {
    if (!accountType) {
      throw new Error('Account type is required for bulk switch.');
    }
    await bulkSwitchGHAProperties({
      file,
      action: action as BulkSwitchGHAPropertiesAction,
      accountType: accountType as GHAAccountType,
    });
  };

  const handleUpdatePropertyType = async (file: File, propertyType: string) => {
    await bulkUpdateGHAPropertyType(file, propertyType as GHAPropertyType);
  };

  const handleBlacklistProperties = async (file: File, action: string) => {
    await bulkBlacklistProperties({ file, action: action as BlacklistAction });
  };

  const handleCugWhitelistProperties = async (file: File, action: string) => {
    await bulkCUGWhitelistProperties({ file, action: action as CUGWhitelistAction });
  };

  const handleCugTierProperties = async (file: File, tier: string) => {
    await bulkSetCugTier({ file, tier: tier as CugTier });
  };

  const handleBulkAppendPricingRegions = async (file: File, _action: string, accountType?: string) => {
    if (!accountType) {
      throw new Error('Account type is required for bulk pricing regions.');
    }
    await bulkAppendPricingRegions({ file, accountType: accountType as GHAAccountType });
  };

  const handleBulkUpdateDiscountRules = async (file: File, _selectedValue: string, accountType?: string) => {
    if (!accountType) {
      throw new Error('Account type is required for bulk discounts.');
    }
    try {
      await bulkUpdateDiscountRules.mutateAsync({
        file,
        accountType: accountType as BulkUpdateGHADiscountRulesAccountType,
      });
    } catch (error) {
      throw new Error(getBulkDiscountDialogErrorMessage(error), { cause: error });
    }
  };

  return (
    <Container maxWidth={false}>
      <Helmet>
        <title>GHA Property Management</title>
      </Helmet>
      <PageSubheader title="GHA Property Management" />
      {!isSupported ? (
        <Box>
          <Alert severity="info">
            <AlertTitle>GHA Not Available</AlertTitle>
            GHA integration is not available for the currently selected brand. GHA is only available for Luxury Escapes
            and Scoopon.
          </Alert>
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end">
            <GHABulkOperationsMenu
              onSwitchProperties={() => setOpenDialog('switch-properties')}
              onUpdatePropertyType={() => setOpenDialog('update-property-type')}
              onBlacklistProperties={() => setOpenDialog('blacklist-properties')}
              onCugWhitelistProperties={() => setOpenDialog('cug-whitelist-properties')}
              onCugTierProperties={() => setOpenDialog('cug-tier-properties')}
              onBulkPricingRegions={() => setOpenDialog('bulk-pricing-regions')}
              onBulkDiscounts={() => setOpenDialog('bulk-discounts')}
            />
          </Box>

          {openDialog === 'switch-properties' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Switch GHA Properties"
              selectLabel="Account Type"
              selectOptions={brandAccountTypeOptions}
              defaultSelectValue={brandAccountTypeOptions[0]?.value}
              radioLabel="Action"
              radioOptions={[
                { value: BulkSwitchGHAPropertiesAction.ENABLE, label: 'Enable on GHA' },
                { value: BulkSwitchGHAPropertiesAction.DISABLE, label: 'Disable on GHA' },
              ]}
              defaultRadioValue={BulkSwitchGHAPropertiesAction.ENABLE}
              submitButtonLabel="Submit"
              successMessage="Bulk operation submitted successfully."
              onSubmit={handleSwitchProperties}
            />
          )}

          {openDialog === 'update-property-type' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Update GHA Property Type"
              radioLabel="GHA Property Type"
              radioOptions={[
                { value: GHAPropertyType.HOTEL, label: 'Hotel' },
                { value: GHAPropertyType.VACATION_RENTAL, label: 'Vacation Rental' },
              ]}
              defaultRadioValue={GHAPropertyType.HOTEL}
              submitButtonLabel="Update GHA Property Type"
              successMessage="Bulk GHA property type update is successful."
              onSubmit={handleUpdatePropertyType}
            />
          )}

          {openDialog === 'blacklist-properties' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Blacklist Properties"
              radioLabel="Action"
              radioOptions={[
                { value: BlacklistAction.BLACKLIST, label: 'Blacklist' },
                { value: BlacklistAction.UNBLACKLIST, label: 'Unblacklist' },
              ]}
              defaultRadioValue={BlacklistAction.BLACKLIST}
              submitButtonLabel="Submit"
              successMessage="Bulk blacklist operation submitted successfully."
              onSubmit={handleBlacklistProperties}
            />
          )}

          {openDialog === 'cug-whitelist-properties' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk CUG Whitelist Properties"
              radioLabel="Action"
              radioOptions={[
                { value: CUGWhitelistAction.WHITELIST, label: 'Whitelist' },
                { value: CUGWhitelistAction.UNWHITELIST, label: 'Block' },
              ]}
              defaultRadioValue={CUGWhitelistAction.WHITELIST}
              submitButtonLabel="Submit"
              successMessage="Bulk CUG whitelist operation submitted successfully."
              onSubmit={handleCugWhitelistProperties}
            />
          )}

          {openDialog === 'cug-tier-properties' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Set Property Tier"
              radioLabel="Property Tier (applied to every row in the CSV)"
              radioOptions={CUG_TIER_OPTIONS.map((tier) => ({ value: tier, label: CUG_TIER_LABELS[tier] }))}
              defaultRadioValue={CUG_TIER_VALUES.TIER_1}
              submitButtonLabel="Set Property Tier"
              successMessage="Bulk property tier update submitted successfully."
              onSubmit={handleCugTierProperties}
            />
          )}

          {openDialog === 'bulk-pricing-regions' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Update Pricing Regions"
              selectLabel="Account Type"
              selectOptions={brandAccountTypeOptions}
              defaultSelectValue={brandAccountTypeOptions[0]?.value}
              csvHeader="propertyId,regions"
              csvRows={['property-123,US|GB', 'property-456,NZ', 'property-789,US|GB|AE', 'property-012,']}
              csvNotes={
                <>
                  • First row must be the column headers: <strong>propertyId,regions</strong>
                  <br />• Each row contains a property ID and pipe-delimited region codes (e.g., US|GB|NZ)
                  <br />• Region codes must be exactly 2 characters (e.g., US, GB, NZ, AE)
                  <br />• Non-empty regions will be appended to existing regions with automatic deduplication
                  <br />• An empty regions column will clear all pricing regions for that property
                </>
              }
              submitButtonLabel="Update Regions"
              successMessage="Bulk pricing regions update submitted successfully."
              onSubmit={handleBulkAppendPricingRegions}
            />
          )}

          {openDialog === 'bulk-discounts' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk discounts"
              selectLabel="Account Type"
              selectOptions={brandAccountTypeOptions}
              defaultSelectValue={brandAccountTypeOptions[0]?.value}
              csvHeader="action,propertyId,minAdvanceBookingWindow,minLengthOfStay,discountType,discountValue,countryDiscounts"
              csvRows={[
                'upsert,property-123,0,1,percentage,10,',
                'upsert,property-456,7,2,margin_retention,3,',
                'upsert,property-789,0,1,,,"{""US"":{""type"":""percentage"",""value"":2},""SG"":{""type"":""margin_retention"",""value"":3}}"',
                'remove,property-012,14,3,,,',
              ]}
              csvNotes={
                <>
                  • First row must be the column headers:{' '}
                  <strong>
                    action,propertyId,minAdvanceBookingWindow,minLengthOfStay,discountType,discountValue,countryDiscounts
                  </strong>
                  <br />• Action is set per CSV row and must be <strong>upsert</strong> or <strong>remove</strong>
                  <br />• Account type is selected in this form and must not be included in the CSV
                  <br />• Universal upsert rows must provide <strong>discountType</strong> and{' '}
                  <strong>discountValue</strong>, with an empty <strong>countryDiscounts</strong> cell
                  <br />• Country-specific upsert rows must leave <strong>discountType</strong> and{' '}
                  <strong>discountValue</strong> empty, and provide JSON in <strong>countryDiscounts</strong>
                  <br />• countryDiscounts JSON keys are uppercase ISO-2 country codes; each value needs{' '}
                  <strong>type</strong> and <strong>value</strong>
                  <br />• JSON cells containing commas must be CSV-quoted, with inner quotes doubled as shown above
                  <br />• Remove rows ignore discountType, discountValue, and countryDiscounts
                </>
              }
              submitButtonLabel="Apply discounts"
              successMessage="Bulk discounts uploaded successfully."
              showSuccessAlert={false}
              showSuccessSnackbar={false}
              showErrorSnackbar={false}
              onSubmit={handleBulkUpdateDiscountRules}
            />
          )}

          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Property Lists
            </Typography>
            <GHAPropertyListMetadata brand={currentBrand as GHAPropertyBrand} />
          </Box>

          <GHAPropertySearch currentBrand={currentBrand as GHAPropertyBrand} />
        </>
      )}
    </Container>
  );
}

export default GHAPropertyManagementContainer;
